import { useCallback, useEffect, useMemo, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PowerUpdate } from "@/components/deck/PowerUpdate";
import { GroupReview, toGroupDraft, groupBlockers, groupPayload } from "@/components/deck/GroupReview";

const GROUP_NAMES = { 1: "Legion Eternals", 2: "We Are Legion I", 3: "Legion Knights", 4: "Legion Warriors", 5: "Legion Scribes" };
const G_KEYS = ["1", "2", "3", "4", "5"];
const latest = (r) => Object.values(r.groups || {}).reduce((m, G) => (String(G.startedAt || "") > m ? String(G.startedAt) : m), "");
const LABEL = { processing: "Reading…", ready: "Ready to check", failed: "Reading failed", applied: "Published", discarded: "Not sent", none: "Not sent" };

export const IntakePanel = ({ open, onClose, onCount }) => {
  const [reviews, setReviews] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [tab, setTab] = useState(null);
  const [busy, setBusy] = useState(null);
  const [mode, setMode] = useState("season"); // CP-005: "season" | "power"
  const [power, setPower] = useState(null);
  const [counts, setCounts] = useState({ season: 0, power: 0 });

  const load = useCallback(() => {
    api.get("/intake/reviews").then((r) => {
      setReviews(r.data);
      const n = r.data.reduce((a, rv) => a + Object.values(rv.groups || {}).filter((G) => G.status === "ready" || G.status === "processing").length, 0);
      setCounts((c) => ({ ...c, season: n }));
    }).catch(() => {});
    api.get("/power/reviews").then((r) => {
      setPower(r.data);
      setCounts((c) => ({ ...c, power: r.data.updates.filter((x) => x.status === "ready" || x.status === "processing").length }));
    }).catch(() => {});
  }, []);
  useEffect(() => { onCount(counts.season + counts.power); }, [counts, onCount]);
  useEffect(() => { load(); const t = setInterval(load, open ? 4000 : 15000); return () => clearInterval(t); }, [load, open]);

  const sel = useMemo(() => [...reviews].sort((a, b) => latest(b).localeCompare(latest(a)))[0], [reviews]);
  const roster = power?.roster || [];
  const groups = sel?.groups || {};
  const stage = (g) => (groups[g] ? groups[g].status : "none");

  // Keep one editable draft per group; a new reading replaces it.
  useEffect(() => {
    setDrafts((d) => {
      const next = { ...d };
      G_KEYS.forEach((g) => {
        const G = groups[g];
        if (G?.status === "ready") { if (!next[g] || next[g].key !== G.finishedAt || next[g].batch !== sel.id) next[g] = { ...toGroupDraft(G), batch: sel.id }; }
        else delete next[g];
      });
      return next;
    });
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (tab && groups[tab] && stage(tab) !== "discarded") return;
    const first = G_KEYS.find((g) => stage(g) === "ready") || G_KEYS.find((g) => groups[g] && stage(g) !== "discarded");
    if (first) setTab(first);
  }, [sel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Players matched in another group's unpublished photos move when that group publishes.
  const pendingFor = (g) => {
    const out = {};
    G_KEYS.forEach((h) => { if (h === g || !drafts[h]) return; drafts[h].rows.forEach((r) => { if (r.include && r.matchId) out[r.matchId] = Number(h); }); });
    return out;
  };
  const ready = G_KEYS.filter((g) => stage(g) === "ready" && drafts[g] && !groupBlockers(groups[g], drafts[g]).length);

  const publish = async (g, quiet) => {
    setBusy(g);
    try {
      const { data } = await api.post(`/intake/${sel.id}/group/${g}/apply`, groupPayload(drafts[g]));
      if (!quiet) toast.success(`Group ${g} published: ${data.updated} updated (${data.moved} moved in), ${data.added} added, ${data.benched} to the Holding Pen${data.bracket ? ", bracket replaced" : ""}`);
      load(); return true;
    } catch (e) { toast.error(`Group ${g}: ${errMsg(e)}`); return false; } finally { setBusy(null); }
  };
  const publishAll = async () => {
    const list = [...ready]; let ok = 0;
    for (const g of list) { if (await publish(g, true)) ok++; }
    if (ok === list.length) toast.success(`Published ${ok} group${ok === 1 ? "" : "s"}. See History for every change.`);
    else if (ok) toast.warning(`Published ${ok} of ${list.length} groups. The others show why above.`);
    else toast.error("Nothing was published. Each group shows why above.");
  };
  const readAgain = async (g) => {
    try { await api.post(`/intake/${sel.id}/group/${g}/read-again`); toast.success(`Reading Group ${g} again…`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!open) return null;
  const G = tab ? groups[tab] : null;
  return (
    <aside className="lg lg-inbox lg-intake" data-testid="intake-panel">
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <h3 style={{ fontSize: 18, margin: 0, marginRight: "auto" }}>{mode === "power" ? "Power update" : "Intake review"}</h3>
        <button className="lg-btn ghost sm" onClick={onClose} data-testid="intake-close"><X size={14} /></button>
      </div>
      <div className="lg-seg" style={{ marginBottom: 12 }}>
        <button className={mode === "season" ? "on" : ""} onClick={() => setMode("season")} data-testid="intake-mode-season">Group updates{counts.season ? ` (${counts.season})` : ""}</button>
        <button className={mode === "power" ? "on" : ""} onClick={() => setMode("power")} data-testid="intake-mode-power">Power update{counts.power ? ` (${counts.power})` : ""}</button>
      </div>
      <div hidden={mode !== "power"}><PowerUpdate data={power} groups={GROUP_NAMES} reload={load} /></div>
      {mode === "season" && <>
        <p style={{ fontSize: 13, color: "var(--muted)", marginBottom: 10 }}>Each group sends its photos from Season Intake. Check one group at a time, tick who to add or move, then publish that group. Nothing changes in the deck until you publish.</p>
        {!sel && <p data-testid="intake-empty">No group has sent photos yet. Groups send from the Season Intake tab.</p>}
        {sel && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
            {G_KEYS.map((g) => (
              <button key={g} className={`lg-gtab ${tab === g ? "on" : ""}`} onClick={() => setTab(g)} disabled={!groups[g]} data-testid={`intake-tab-${g}`}>
                <span className={`lg-dot ${stage(g)} ${stage(g) === "ready" && drafts[g] && groupBlockers(groups[g], drafts[g]).length ? "blocked" : ""}`} />G{g} · {GROUP_NAMES[g]} <small>{stage(g) === "ready" && drafts[g] && groupBlockers(groups[g], drafts[g]).length ? "Needs a fix" : LABEL[stage(g)] || stage(g)}</small>
              </button>
            ))}
            {ready.length > 1 && <button className="lg-btn sm" style={{ marginLeft: "auto" }} onClick={publishAll} disabled={!!busy} data-testid="intake-publish-all">Publish all ready groups ({ready.length})</button>}
          </div>
        )}
        {G && G.status === "processing" && <p className="lg-note" data-testid="intake-processing"><Loader2 size={14} className="lg-spin" /> Reading Group {tab}'s photos… this takes about a minute.</p>}
        {G && G.status === "failed" && <><p className="lg-err" data-testid="intake-failed">{G.error}</p><button className="lg-btn ghost sm" onClick={() => readAgain(tab)}>Read again</button></>}
        {G && G.status === "applied" && <p className="lg-note" data-testid="intake-applied">Group {tab} published {new Date(G.appliedAt).toLocaleString()} by {G.appliedBy}: {G.result?.updated} updated ({G.result?.moved} moved in), {G.result?.added} added, {G.result?.benched} moved to the Holding Pen{G.result?.bracket ? ", bracket replaced" : ""}. History has every change.</p>}
        {G && G.status === "discarded" && <p className="lg-note">Group {tab} reopened its photos in Season Intake. It shows here again after it's sent.</p>}
        {G && G.status === "ready" && drafts[tab] && (
          <GroupReview g={tab} title={GROUP_NAMES[tab]} G={G} draft={drafts[tab]} setDraft={(d) => setDrafts((x) => ({ ...x, [tab]: d }))}
            roster={roster} pendingIn={pendingFor(tab)} busy={busy === tab} onPublish={() => publish(tab)} onReadAgain={() => readAgain(tab)} />
        )}
      </>}
    </aside>
  );
};
